import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Newspaper } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import type { ReleaseNote } from '@/services/releaseNotes/releaseNotesApi'
import { useCurrentUser } from '../contexts/UserContext'
import { useWhatsNew } from '../hooks/useWhatsNew'
import { ReleaseNoteCard } from './ReleaseNoteCard'

/**
 * Whom the popup has greeted since the page loaded. Every page has its own
 * shell, so without this a popup closed before its read mark was saved would
 * open again on the next page; keyed by user so the next person to sign in
 * on the same tab is still greeted.
 */
let greetedUserId: string | null = null

function NoteList({ notes, newIds }: { notes: ReleaseNote[]; newIds: Set<string> }) {
  return (
    <div className="flex flex-col gap-6">
      {notes.map((note, i) => (
        <Fragment key={note.id}>
          {i > 0 && <Separator />}
          <ReleaseNoteCard
            title={note.title}
            subTitle={note.subTitle}
            publishTime={note.publishTime}
            internal={note.userGroups.length > 0}
            apps={note.apps}
            tags={note.tags}
            content={note.content}
            isNew={newIds.has(note.id)}
          />
        </Fragment>
      ))}
    </div>
  )
}

function WhatsNewIcon() {
  return (
    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-(--ad-primary-soft) text-(--ad-primary)">
      <Newspaper className="size-5" />
    </span>
  )
}

/**
 * The header's "What's new" button, which opens every release note, and the
 * popup that greets a user with the ones they have not seen yet. Seeing them
 * marks them read, so each note greets them once.
 */
export function WhatsNew() {
  const { t } = useTranslation()
  const user = useCurrentUser()
  const { notes, unread, isLoading, markRead, isMasquerading } = useWhatsNew()
  const [open, setOpen] = useState(false)
  /**
   * The unread notes greeting the user, or every note. Kept when the dialog
   * closes so its content does not change while it fades out.
   */
  const [view, setView] = useState<'new' | 'all'>('new')
  /**
   * The notes that were new when the popup or the list opened. Reading them
   * empties `unread` straight away, but they should stay marked as new while
   * they are on screen.
   */
  const [shownAsNew, setShownAsNew] = useState<ReleaseNote[]>([])
  const newIds = new Set(shownAsNew.map((note) => note.id))

  useEffect(() => {
    if (!user || greetedUserId === user.id) return
    if (isMasquerading || isLoading || unread.length === 0) return
    greetedUserId = user.id
    setShownAsNew(unread)
    setView('new')
    setOpen(true)
  }, [user, isLoading, isMasquerading, unread])

  const close = () => {
    setOpen(false)
    markRead()
  }

  const openAll = () => {
    if (!open) setShownAsNew(unread)
    setView('all')
    setOpen(true)
    markRead()
  }

  const label =
    unread.length > 0 ? t('whatsNew.buttonUnread', { count: unread.length }) : t('whatsNew.title')

  return (
    <>
      <button className="ad-topbar__iconbtn" title={label} aria-label={label} onClick={openAll}>
        <Newspaper className="size-4" />
        {unread.length > 0 && <span className="ad-topbar__badge">{unread.length}</span>}
      </button>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && close()}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl">
          <DialogHeader className="flex-row items-center gap-3 border-b px-6 py-5 text-left">
            <WhatsNewIcon />
            <div className="flex flex-col gap-1">
              <DialogTitle>{t('whatsNew.title')}</DialogTitle>
              <DialogDescription>
                {view === 'all'
                  ? t('whatsNew.sheetDescription')
                  : t('whatsNew.popupDescription', { count: shownAsNew.length })}
              </DialogDescription>
            </div>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {view === 'new' ? (
              <NoteList notes={shownAsNew} newIds={new Set()} />
            ) : isLoading ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : notes.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('whatsNew.empty')}</p>
            ) : (
              <NoteList notes={notes} newIds={newIds} />
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4 sm:justify-between">
            {view === 'new' ? (
              <Button type="button" variant="ghost" onClick={openAll}>
                {t('whatsNew.seeAll')}
              </Button>
            ) : (
              <span />
            )}
            <Button type="button" onClick={close}>
              {view === 'new' ? t('whatsNew.gotIt') : t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
